'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Lightbulb } from 'lucide-react';
import { LogoV2 } from './logo-v2';
import { StreakDaysComponent } from '@/components/streak-days-component';
import { UserAvatarV2 } from './user-avatar-v2';
import { ReadingSizeSelector } from './reading-size-selector';
import { PitchDialogComponent } from '@/components/onboarding/pitch-dialog-component';
import { useAuth } from '@/contexts/authContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/hooks/firebase.client.config';

interface HeaderV2Props {
  // streakCount is no longer used, StreakDaysComponent fetches its own data
}

export function HeaderV2(props: HeaderV2Props) {
  const [showPitchOverlay, setShowPitchOverlay] = useState(false);
  const [hasLists, setHasLists] = useState(false);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const router = useRouter();
  const { userInfo } = useAuth();

  // Check if user has lists when authenticated
  useEffect(() => {
    if (!userInfo?.uid) {
      setHasLists(false);
      return;
    }

    const checkUserLists = async () => {
      try {
        setIsLoadingLists(true);
        // Read user document to get listIds array
        const userDoc = await getDoc(doc(db, 'users', userInfo.uid));
        const userData = userDoc.data();

        // User has lists if listIds array exists and has items
        setHasLists(!!userData?.listIds && userData.listIds.length > 0);
      } catch (error) {
        console.error('Error checking user lists:', error);
        setHasLists(false);
      } finally {
        setIsLoadingLists(false);
      }
    };

    checkUserLists();
  }, [userInfo?.uid]);

  const handleGetStarted = () => {
    if (!userInfo) {
      router.push('/signin');
    } else if (!hasLists) {
      router.push('/watchlist');
    } else {
      router.push('/signup');
    }
  };

  return (
    <>
      {/* Pitch Dialog */}
      <PitchDialogComponent
        isOpen={showPitchOverlay}
        onClose={() => setShowPitchOverlay(false)}
        onGetStarted={handleGetStarted}
        isAuthenticated={!!userInfo}
        hasLists={hasLists}
      />

      <header className="sticky top-0 z-50 border-b border-slate-700/30 bg-[#0d1117]/95 backdrop-blur-xl">
        {/* Subtle gold accent line at top */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#d4a853]/60 to-transparent" />

        <div className="flex justify-between items-center px-4 md:px-6 py-3.5">
          <LogoV2 />
          <div className="flex items-center gap-3">
            <ReadingSizeSelector />
            {/* Pitch Lightbulb Icon - Only show for non-logged-in users */}
            {!userInfo && (
              <button
                onClick={() => setShowPitchOverlay(true)}
                className="p-2 rounded-lg text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 transition-all duration-200"
                aria-label="Learn about TradedNews"
                title="Learn about TradedNews"
              >
                <Lightbulb className="w-5 h-5" />
              </button>
            )}
            <div className="w-px h-5 bg-slate-700/50 hidden sm:block" />
            <StreakDaysComponent userId={userInfo?.uid} />
            <UserAvatarV2 />
          </div>
        </div>
      </header>
    </>
  );
}
